import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import useScrollReveal from '@/hooks/useScrollReveal';

const ContactPage = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const heroRef = useScrollReveal();
  const formRef = useScrollReveal();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d763dakbmgf2o8mm70s0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[45vh] flex items-center bg-charcoal overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/a211ad15d457e2d7e461e9243227dd2a.jpg"
            alt="Contact OST-C vocational training center Arlington TX"
            className="w-full h-full object-cover opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/95 to-charcoal/70" />
        </div>
        <div ref={heroRef} className="reveal relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-14 w-full">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-[2px] bg-gold" />
            <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">Get In Touch</span>
          </div>
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-white leading-[1.1] mb-4 max-w-2xl">
            Start Your Enrollment at OST-C Today
          </h1>
          <p className="font-inter text-base text-white/60 max-w-xl leading-relaxed">
            Have questions about programs, eligibility, or scheduling? Contact our team and we&apos;ll help you find the right path forward — fast.
          </p>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-charcoal border-y border-white/10 py-5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {[
              { icon: 'ri-reply-line', label: 'Same-Day Response' },
              { icon: 'ri-shield-check-line', label: 'Confidential & No Obligation' },
              { icon: 'ri-calendar-check-line', label: 'Fast Enrollment Process' },
              { icon: 'ri-user-follow-line', label: 'Personalized Program Match' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <div className="w-7 h-7 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-gold text-base`} />
                </div>
                <span className="font-inter text-[13px] font-medium text-white/70 whitespace-nowrap">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="bg-cream py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left — info */}
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-[2px] bg-gold" />
                <span className="font-inter text-gold text-xs font-semibold tracking-[0.25em] uppercase">Contact Information</span>
              </div>
              <h2 className="font-playfair text-3xl font-bold text-charcoal mb-6 leading-tight">
                We&apos;re Here When You&apos;re Ready
              </h2>
              <p className="font-inter text-base text-charcoal/60 leading-relaxed mb-8">
                Whether you&apos;re ready to enroll, have questions about a specific program, or just want to learn more — our team is available and committed to helping you take the next step.
              </p>

              <div className="flex flex-col gap-5 mb-10">
                {[
                  { icon: 'ri-phone-line', label: 'Phone', value: '469-853-5038', href: 'tel:4698535038', sub: 'Call during business hours for fast assistance' },
                  { icon: 'ri-map-pin-line', label: 'Location', value: 'Arlington, TX 76006', sub: 'DFW Metroplex — Centrally Located' },
                  { icon: 'ri-time-line', label: 'Hours', value: 'Mon–Fri: 8:00 AM – 6:00 PM', sub: 'Appointments strongly recommended' },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4">
                    <div className="w-11 h-11 flex items-center justify-center bg-gold/10 rounded-lg shrink-0">
                      <i className={`${item.icon} text-gold text-lg`} />
                    </div>
                    <div>
                      <p className="font-inter text-xs font-semibold text-charcoal/40 uppercase tracking-wider mb-0.5">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="font-inter font-semibold text-charcoal text-sm hover:text-gold transition-colors cursor-pointer">{item.value}</a>
                      ) : (
                        <p className="font-inter font-semibold text-charcoal text-sm">{item.value}</p>
                      )}
                      <p className="font-inter text-xs text-charcoal/50">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Map */}
              <div className="w-full h-[260px] rounded-lg overflow-hidden border border-cream-100">
                <iframe
                  title="OST-C Contact Location - Arlington TX Occupational Training"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d107038.13156764076!2d-97.24817845!3d32.71372745!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864e7d1b6bd80635%3A0x2cfaf5d5ef36e0c0!2sArlington%2C%20TX!5e0!3m2!1sen!2sus!4v1712000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            </div>

            {/* Right — Form */}
            <div ref={formRef} className="reveal">
              <div className="bg-white rounded-lg border border-cream-100 p-8 md:p-10 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-gold" />
                <h3 className="font-playfair text-2xl font-bold text-charcoal mb-2">Enrollment Request Form</h3>
                <p className="font-inter text-sm text-charcoal/55 mb-7">Fill out the form below and our team will reach out within one business day.</p>

                {submitted ? (
                  <div className="bg-gold/5 border border-gold/30 rounded-lg p-8 text-center">
                    <div className="w-14 h-14 flex items-center justify-center bg-gold/10 rounded-full mx-auto mb-4">
                      <i className="ri-checkbox-circle-fill text-gold text-2xl" />
                    </div>
                    <p className="font-playfair text-xl font-bold text-charcoal mb-2">Request Received!</p>
                    <p className="font-inter text-sm text-charcoal/60">
                      Thank you for reaching out. Our team will contact you within one business day to discuss your program options.
                    </p>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    id="ostc-contact-form"
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block font-inter text-xs font-semibold text-charcoal/50 uppercase tracking-wider mb-2">Full Name *</label>
                        <input
                          required
                          type="text"
                          name="name"
                          placeholder="Your full name"
                          className="w-full bg-cream border border-cream-100 text-charcoal placeholder:text-charcoal/35 font-inter text-sm px-4 py-3 rounded-md focus:outline-none focus:border-gold/50 transition-colors duration-200"
                        />
                      </div>
                      <div>
                        <label className="block font-inter text-xs font-semibold text-charcoal/50 uppercase tracking-wider mb-2">Phone *</label>
                        <input
                          required
                          type="tel"
                          name="phone"
                          placeholder="Your phone number"
                          className="w-full bg-cream border border-cream-100 text-charcoal placeholder:text-charcoal/35 font-inter text-sm px-4 py-3 rounded-md focus:outline-none focus:border-gold/50 transition-colors duration-200"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block font-inter text-xs font-semibold text-charcoal/50 uppercase tracking-wider mb-2">Email *</label>
                      <input
                        required
                        type="email"
                        name="email"
                        placeholder="your@email.com"
                        className="w-full bg-cream border border-cream-100 text-charcoal placeholder:text-charcoal/35 font-inter text-sm px-4 py-3 rounded-md focus:outline-none focus:border-gold/50 transition-colors duration-200"
                      />
                    </div>
                    <div>
                      <label className="block font-inter text-xs font-semibold text-charcoal/50 uppercase tracking-wider mb-2">Program of Interest</label>
                      <select
                        name="program"
                        className="w-full bg-cream border border-cream-100 text-charcoal/80 font-inter text-sm px-4 py-3 rounded-md focus:outline-none focus:border-gold/50 transition-colors duration-200 cursor-pointer"
                      >
                        <option value="">Select a program...</option>
                        <option value="Vocational Skills Training">Vocational Skills Training</option>
                        <option value="Job Readiness & Career Development">Job Readiness &amp; Career Development</option>
                        <option value="Life Skills Development">Life Skills Development</option>
                        <option value="Employment Placement Support">Employment Placement Support</option>
                        <option value="Community Reintegration">Community Reintegration</option>
                        <option value="Workforce Development Training">Workforce Development Training</option>
                        <option value="Not sure yet">Not Sure Yet — Help Me Choose</option>
                      </select>
                    </div>
                    <div>
                      <label className="block font-inter text-xs font-semibold text-charcoal/50 uppercase tracking-wider mb-2">Message</label>
                      <textarea
                        name="message"
                        rows={4}
                        maxLength={500}
                        placeholder="Tell us about your goals, background, or any questions you have..."
                        className="w-full bg-cream border border-cream-100 text-charcoal placeholder:text-charcoal/35 font-inter text-sm px-4 py-3 rounded-md focus:outline-none focus:border-gold/50 transition-colors duration-200 resize-none"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-gold hover:bg-gold-dark disabled:opacity-60 text-charcoal font-inter font-bold text-sm py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      {loading ? 'Sending...' : 'Submit Enrollment Request'}
                    </button>
                    <p className="font-inter text-xs text-charcoal/40 text-center">
                      Confidential &bull; No obligation &bull; Typically responded to within 1 business day
                    </p>
                  </form>
                )}
              </div>

              {/* Quick action */}
              <div className="mt-5 bg-charcoal rounded-lg p-5 flex items-center gap-4">
                <div className="w-11 h-11 flex items-center justify-center bg-gold/10 rounded-lg shrink-0">
                  <i className="ri-phone-fill text-gold text-lg" />
                </div>
                <div className="flex-1">
                  <p className="font-inter text-sm font-semibold text-white">Prefer to call?</p>
                  <p className="font-inter text-xs text-white/50">Speak with a program advisor directly</p>
                </div>
                <a
                  href="tel:4698535038"
                  className="bg-gold hover:bg-gold-dark text-charcoal font-inter font-bold text-sm px-5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-all duration-200"
                >
                  469-853-5038
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Programs reminder */}
      <section className="bg-white py-16 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="font-playfair text-3xl font-bold text-charcoal mb-4">
            Not Sure Which Program Is Right for You?
          </h2>
          <p className="font-inter text-base text-charcoal/60 mb-8 max-w-xl mx-auto">
            Our advisors will walk you through every program option and help you find the best fit for your goals, background, and timeline.
          </p>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 border-2 border-charcoal hover:border-gold hover:text-gold text-charcoal font-inter font-semibold text-sm px-8 py-3.5 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
          >
            Explore All Programs
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default ContactPage;
